import { Stack } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKey } from '../../../types/i18n';
import StyledChatAgentStep from '../../../styles/StyledChatAgentStep';
import StyledFormItem from '../../../styles/StyledFormItem';
import AutocompleteInput from '../../shared/AutoCompleteInput/AutoCompleteInput';
import FormSettingsPreview from '../../shared/FormSettingsPreview/FormSettingsPreview';
import Input from '../../shared/Input/Input';
import Text from '../../shared/Text/Text';
import Textarea from '../../shared/Textarea/Textarea';
import ToolTip from '../../shared/ToolTip/ToolTip';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';

const userDataFieldLabels: Record<string, TranslationKey> = {
  name: 'Name',
  email: 'Email',
  company: 'Company',
  phone: 'Phone',
};

interface ChatAgentFormSettingsStepProps {
  stepLabel?: string;
  label?: string;
  description?: string;
}
const ChatAgentFormSettingsStep: React.FC<ChatAgentFormSettingsStepProps> = ({
  stepLabel,
  label,
  description,
}) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();

  const formData = useWatch();

  return (
    <StyledChatAgentStep>
      <Stack direction="column" width="364px">
        <WorkflowStepLabel
          stepLabel={stepLabel}
          label={label ?? t('Chat Agent Form Settings')}
          description={description}
        />
        <StyledFormItem>
          <Text type="label">{t('Form Title')}</Text>
          <Input
            name="user_data_prompt_title"
            withError={!!errors.user_data_prompt_title}
            helperText={
              !!errors.user_data_prompt_title ? t('Form title is required') : ''
            }
          />
        </StyledFormItem>
        <StyledFormItem>
          <Text type="label">{t('Form Button Label')}</Text>
          <Input
            name="user_data_prompt_button"
            withError={!!errors.user_data_prompt_button}
            helperText={
              !!errors.user_data_prompt_button
                ? t('Form button label is required')
                : ''
            }
          />
        </StyledFormItem>
        <StyledFormItem>
          <Text type="label">{t('Form User Data')}</Text>
          <AutocompleteInput
            name="user_data_prompt"
            options={['name', 'email', 'company', 'phone']}
            initialValue={[]}
            errorMessage={t('Form user data is required')}
            customOptionEnabled={false}
            getOptionLabel={option => {
              const labelKey = userDataFieldLabels[option];
              return labelKey ? t(labelKey) : option;
            }}
          />
        </StyledFormItem>
        <StyledFormItem>
          <Text type="label">
            {t('Disclaimer')}
            <ToolTip
              title={t(
                'Add a legal disclaimer or privacy notice. Accepts Markdown content. Users can read it by clicking the Disclaimer link in the contact form.'
              )}
            />
          </Text>
          <Textarea
            name="user_data_prompt_html_disclaimer"
            placeholder={t('Enter the disclaimer here')}
            minRows={8}
            maxRows={16}
            maxLength={1500}
            withError={!!errors.user_data_prompt_html_disclaimer}
            helperText={
              !!errors.user_data_prompt_html_disclaimer
                ? t('Disclaimer is required')
                : ''
            }
          />
        </StyledFormItem>
      </Stack>
      <FormSettingsPreview
        user_data_prompt={formData.user_data_prompt}
        user_data_prompt_button={formData.user_data_prompt_button}
        user_data_prompt_title={formData.user_data_prompt_title}
        disclaimerHtml={formData.user_data_prompt_html_disclaimer}
      />
    </StyledChatAgentStep>
  );
};

export default ChatAgentFormSettingsStep;
